import ShippingMethod from '@/constructs/ShippingMethod';
import config from './config';

/** Contains configs related to shipping methods. */
const shippingMethods = config({
  /** Defaults for all sites and locales. */
  default: {
    /** Default settings for all languages. */
    default: {
      /** Contains configs related to caching. */
      caching: {
        /**
         * The TTL for the cache used in the isomorphic ShippingMethodService.
         */
        isomorphicCacheTTL: 30,

        /**
         * The TTL for the cache used in the ServerShippingMethodService.
         */
        serverCacheTTL: 30
      },

      /**
       * Contains overrides for specific attributes of shipping methods.
       */
      overrides: {
        /**
         * Price overrides. Value should be a number with the amount to
         * charge in the current locale's currency.
         */
        prices: {
          [ShippingMethod.STG]: 8,
          [ShippingMethod.STG2]: 8,
          [ShippingMethod.ST2]: 20,
          [ShippingMethod.STO]: 30,
          [ShippingMethod.USPSGround]: 10,
          [ShippingMethod.SFSIntl]: 75
        },

        /**
         * Delivery business day overrides. Value should be an object with
         * earliest and latest delivery date in business days.
         *
         * @example
         * `[ShippingMethod.STG]: { earliest: 3, latest: 6 }`
         *
         * Orders shipped with the STG shipping method will arrive in 3 to 6
         * business days.
         */
        deliveryBusinessDayRanges: {
          [ShippingMethod.STG]: { earliest: 3, latest: 6 },
          [ShippingMethod.STG2]: { earliest: 3, latest: 6 },
          [ShippingMethod.ST2]: { earliest: 2, latest: 3 },
          [ShippingMethod.STO]: { earliest: 1, latest: 2 },
          [ShippingMethod.USPSGround]: { earliest: 3, latest: 6 }
        },

        /**
         * If `true`, overrides upstream delivery messages with simple "Arrives
         * in X-Y days" format generated from business day ranges.
         *
         * When enabled:
         * - `formattedDeliveryEstimate` will be replaced with a simple message
         *   like "Arrives in 3-6 business days" based on
         *   `deliveryBusinessDayRanges`.
         * - `deliveryMessageHTML` will be set to `null` (no HTML prefix).
         * - `additionalMessage` will be set to `null` (no additional shipping
         *   message).
         *
         * This provides a clean, consistent delivery message across all
         * shipping methods without upstream formatting or promotional content.
         */
        useSimpleDeliveryMessages: false,

        /**
         * The number of hours before the cutoff time to start displaying
         * the cutoff time to the user.
         */
        cutoffDisplayHours: 4,

        /**
         * If `true`, the shipping method will attempt to show the earliest
         * arrival date in checkout.
         */
        showEarliestArrivalDate: true,

        /**
         * Countdown related settings for shipping methods.
         */
        countdownFormat: {
          /**
           * Whether to show seconds in the countdown timer.
           */
          showSeconds: true,
          /**
           * If `true`, the countdown will show minutes.
           */
          showMinutes: true,
          /**
           * If `true`, the countdown will show hours.
           */
          showHours: true,
          /**
           * If `true`, the countdown will show days.
           */
          showDays: false
        }
      },

      /**
       * If `true`, the shipping method will show a holiday shipping message in checkout.
       */
      isHolidayShippingMessagingEnabled: false
    }
  },
  'AHNU-US': { default: { isHolidayShippingMessagingEnabled: false } },
  'TEVA-US': {
    default: {
      overrides: {
        /**
         * Price overrides. Value should be a number with the amount to
         * charge in the current locale's currency.
         */
        prices: {
          [ShippingMethod.STG]: 10,
          [ShippingMethod.STG2]: 10,
          [ShippingMethod.ST2]: 17,
          [ShippingMethod.STO]: 28,
          [ShippingMethod.USPSGround]: 10
        },
        /**
         * Countdown related settings for shipping methods.
         */
        countdownFormat: {
          /**
           * Whether to show seconds in the countdown timer.
           */
          showSeconds: false,
          /**
           * If `true`, the countdown will show minutes.
           */
          showMinutes: true,
          /**
           * If `true`, the countdown will show hours.
           */
          showHours: true,
          /**
           * If `true`, the countdown will show days.
           */
          showDays: false
        }
      }
    }
  }
});

export default shippingMethods;
